import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ColorPickerItem} from './colorPickerItem';

/*
 * Types.
 */

export enum ColorPickerModesEnum {
  COLOR = 'Color',
  FULL_BLANK = 'FullBlank',
  FULL_WHITE = 'FullWhite',
  WITH_GREY = 'WithGrey',
}

export enum ColorPickerLayoutsEnum {
  ONE_LINE = 'ONE_LINE',
  TWO_LINES = 'TWO_LINES',
}

interface ColorItem {
  key: string;
  paletteColor?: PaletteColorsEnum;
  color: string;
  hoverColor: string;
  shouldAddBorder: boolean;
}

/*
 * Props.
 */

export type ColorSelectHandler = (color: string, paletteColor?: PaletteColorsEnum) => void;

export interface ColorPickerProps {
  color?: string;
  mode?: ColorPickerModesEnum;
  layout?: ColorPickerLayoutsEnum;
  onColorSelect?: ColorSelectHandler;
}

interface ColorPickerStyleProps {
  $layout?: ColorPickerLayoutsEnum;
}

/*
 * Style.
 */

const StyledPickerDiv = styled.div<ColorPickerStyleProps>`
  display: grid;
  grid-auto-flow: column;
  grid-gap: 12px;
  ${(p) => addTemplateRowsStyle(p)};
`;

function addTemplateRowsStyle(props: ColorPickerStyleProps) {
  return props.$layout === ColorPickerLayoutsEnum.ONE_LINE
    ? css`
        grid-template-rows: repeat(1, auto);
      `
    : css`
        grid-template-rows: repeat(2, auto);
      `;
}

/*
 * Component.
 */

export const ColorPicker: FC<ColorPickerProps> = (props) => {
  const theme = useUserTheme();
  const mode = props.mode || ColorPickerModesEnum.COLOR;
  const colorItems = buildColorItems(mode, theme);
  return (
    <StyledPickerDiv $layout={props.layout}>
      {colorItems.map((item) => renderItem(props, item))}
    </StyledPickerDiv>
  );
};

const paletteColors = [
  PaletteColorsEnum.PINK,
  PaletteColorsEnum.RED,
  PaletteColorsEnum.ORANGE,
  PaletteColorsEnum.YELLOW,
  PaletteColorsEnum.GREEN,
  PaletteColorsEnum.TEAL,
  PaletteColorsEnum.BLUE,
  PaletteColorsEnum.PURPLE,
];

function buildColorItems(mode: ColorPickerModesEnum, theme: UserTheme) {
  const includeBlack = [ColorPickerModesEnum.FULL_BLANK, ColorPickerModesEnum.FULL_WHITE].includes(mode);
  const includeWhite = mode === ColorPickerModesEnum.FULL_WHITE;
  const includeBlank = mode === ColorPickerModesEnum.FULL_BLANK;
  const includeGrey = mode === ColorPickerModesEnum.WITH_GREY;

  // All modes get palette colors
  const colorItems: Array<ColorItem> = paletteColors.map((v) => ({
    key: theme.palette[v].shade40,
    paletteColor: v,
    color: theme.palette[v].shade40,
    hoverColor: theme.palette[v].shade20,
    shouldAddBorder: false,
  }));

  if (includeBlack) {
    colorItems.push({
      key: theme.greys.shade90,
      paletteColor: PaletteColorsEnum.GREY,
      color: theme.greys.shade90,
      hoverColor: theme.greys.shade30,
      shouldAddBorder: false,
    });
  }

  if (includeWhite) {
    colorItems.push({
      key: theme.greys.white,
      paletteColor: PaletteColorsEnum.GREY,
      color: theme.greys.white,
      hoverColor: theme.greys.shade30,
      shouldAddBorder: true,
    });
  }

  if (includeBlank) {
    colorItems.push({
      key: 'blank',
      paletteColor: undefined,
      color: '',
      hoverColor: theme.greys.shade30,
      shouldAddBorder: true,
    });
  }

  if (includeGrey) {
    colorItems.push({
      key: theme.palette[PaletteColorsEnum.GREY].shade40,
      paletteColor: PaletteColorsEnum.GREY,
      color: theme.palette[PaletteColorsEnum.GREY].shade40,
      hoverColor: theme.palette[PaletteColorsEnum.GREY].shade20,
      shouldAddBorder: false,
    });
  }

  return colorItems;
}

function renderItem(props: ColorPickerProps, item: ColorItem) {
  const {key, paletteColor, color, hoverColor, shouldAddBorder} = item;
  return (
    <ColorPickerItem
      key={key}
      color={color}
      hoverColor={hoverColor}
      shouldAddBorder={shouldAddBorder}
      isSelected={props.color === color}
      onClick={() => props.onColorSelect && props.onColorSelect(color, paletteColor)}
    />
  );
}
